import moment from 'moment';

/**
 * Obecné pomocné funkce pro agregace (seskupování, součty, generování časových klíčů).
 * Cíl: čisté (pure) funkce bez vedlejších efektů – snadno testovatelné a znovupoužitelné.
 */

/**
 * Vrátí denní klíč ve formátu YYYY-MM-DD.
 * Důvod: sjednocení klíčů v mapách pro denní trendy a heatmapy.
 */
export function toDayKey(dateIso: string | Date): string {
    return moment(dateIso).format('YYYY-MM-DD');
}

/**
 * Vrátí měsíční klíč (první den měsíce) ve formátu YYYY-MM-01.
 */
export function toMonthKey(dateIso: string | Date): string {
    return moment(dateIso).startOf('month').format('YYYY-MM-01');
}

/**
 * Seskupí položky dle klíče. Pokud klíč neexistuje, vytvoří novou skupinu.
 */
export function groupByKey<T, K extends string | number | null>(items: T[], keyFn: (item: T) => K): Map<K, T[]> {
    const map = new Map<K, T[]>();
    for (const it of items) {
        const key = keyFn(it);
        const arr = map.get(key) || [];
        arr.push(it);
        map.set(key, arr);
    }
    return map;
}

/**
 * Bezpečný součet s převodem na číslo a ignorací NaN.
 */
export function sumBy<T>(items: T[], selector: (item: T) => number): number {
    let sum = 0;
    for (const it of items) {
        const v = Number(selector(it) || 0);
        if (!isNaN(v as any)) sum += v;
    }
    return sum;
}

/**
 * Průměr pouze z platných čísel. Pokud není co průměrovat, vrací null.
 */
export function average(values: number[]): number | null {
    const valid = values.filter((v) => typeof v === 'number' && isFinite(v));
    if (valid.length === 0) return null;
    const s = valid.reduce((a, b) => a + b, 0);
    return s / valid.length;
}

/**
 * Omezí hodnotu do intervalu 0..1. Pokud není číslo, vrací null.
 */
export function clamp01(value: number | null): number | null {
    if (value == null || isNaN(value as any)) return null;
    return Math.max(0, Math.min(1, value));
}

/**
 * Vytvoří 2D mřížku (např. 7×24 pro heatmapu dnů×hodin) inicializovanou konstantou.
 */
export function createGrid(rows: number, cols: number, init = 0): number[][] {
    return Array.from({ length: rows }, () => Array.from({ length: cols }, () => init));
}

/**
 * Vrátí indexy top-N hodnot (sestupně dle hodnoty). Hodí se pro výběr top hodin/dnů.
 */
export function topNIndexes(values: number[], n: number): number[] {
    return values
        .map((v, i) => ({ v, i }))
        .sort((a, b) => b.v - a.v)
        .slice(0, n)
        .map((x) => x.i);
}


